import { SwapDownIcon } from "@src/assets";
import { RoutePath } from "@src/interfaces";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banner,
  ExpandableFees,
  ExternalAddressCard,
  FeesWarningBanner,
} from "@src/components";
import { useEffect, useMemo } from "react";
import { useSwapContext } from "@src/contexts";
import { useTokenBalance } from "@src/hooks";
import { PoweredByPanel } from "../../components/PoweredByPanel";
import { AmountPanel, SwapButton } from "@src/features";
import useGasFees from "@src/hooks/useGasFees";

export type SwapPanelType = "source" | "destination";
interface Props {
  tab: RoutePath;
}
export const Swap = ({ tab }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleChangeTab = () => {
    location.pathname === RoutePath.Sell
      ? navigate(RoutePath.Buy)
      : navigate(RoutePath.Sell);
  };

  const {
    isFetchingRoute,
    token,
    tokenAmount,
    errorDetails,
    isExternalMode,
    integrationConfig,
    feesToTokenValueRatio,
    acceptedHighFees,
    setAcceptedHighFees,
    setDisplayToken,
  } = useSwapContext();

  useEffect(() => {
    setDisplayToken(undefined);
  }, [setDisplayToken]);

  const tokenBalances = useTokenBalance({ token });
  const { gasFees } = useGasFees();
  const isAmountValid = useMemo(() => {
    if (location.pathname === RoutePath.Buy) {
      return true;
    }
    if (
      location.pathname === RoutePath.Sell &&
      Number(tokenBalances.humanReadable) < Number(tokenAmount)
    ) {
      return false;
    }
    return true;
  }, [location.pathname, tokenBalances, tokenAmount]);

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex-1 flex flex-col gap-2 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-[4px]">
          <div
            className={`flex flex-col rounded-[12px] ${
              isAmountValid ? "bg-card_bg" : "bg-error_color/10"
            }`}
          >
            <AmountPanel
              type={"source"}
              tokenType={tab === RoutePath.Buy ? "cash" : "token"}
            />
          </div>

          <div
            className={`relative ${!isExternalMode && "cursor-pointer"}`}
            onClick={() => {
              !isExternalMode && handleChangeTab();
            }}
          >
            <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[25px] h-[25px]">
              <SwapDownIcon />
            </div>
          </div>
          <div className="flex flex-col rounded-[12px] bg-theme_color bg-opacity-20">
            <AmountPanel
              type={"destination"}
              tokenType={tab === RoutePath.Sell ? "cash" : "token"}
            />
          </div>
        </div>
        <ExpandableFees gasFees={gasFees} isFetchingRoute={isFetchingRoute} />
        {isExternalMode && (
          <ExternalAddressCard
            address={integrationConfig.externalWalletAddress}
          />
        )}
        {errorDetails && (
          <Banner>
            <div className="flex flex-col items-start text-left">
              {errorDetails}
            </div>
          </Banner>
        )}
        <FeesWarningBanner
          feesToTokenValueRatio={feesToTokenValueRatio}
          confirmed={acceptedHighFees}
          setConfirmed={setAcceptedHighFees}
        />
      </div>
      <div className="flex flex-col gap-2">
        <SwapButton />
        <PoweredByPanel />
      </div>
    </div>
  );
};
